"use client";

import { Lock } from "lucide-react";
import { lazy, Suspense, type ReactNode } from "react";

import { Badge } from "../data-display/badge";
import { ProblemNotice, type ProblemResolution } from "../feedback/problem-notice";
import { Skeleton } from "../feedback/skeleton";
import { Button } from "../inputs/button";
import type { FileEditorDiagnostic, FileEditorLanguage } from "../inputs/file-editor/types";
import { cn } from "../utils";

// Lazy so importing the pane does not pull CodeMirror into the main bundle.
const FileEditor = lazy(() => import("../inputs/file-editor/file-editor"));

/**
 * "saving" covers an edit waiting to be written as well as a write in flight; "invalid" is an
 * edit held back because the file has problems.
 */
export type FileEditorPaneStatus =
  | "saved"
  | "saving"
  | "failed"
  | "read-only"
  | "conflict"
  | "invalid";

export interface FileEditorPaneReadOnlyNotice {
  /** What makes the file read-only. */
  reason: ReactNode;
  howToResolve: ReactNode;
  resolution: ProblemResolution;
}

export interface FileEditorPaneProps {
  path: string;
  value: string;
  language: FileEditorLanguage;
  status: FileEditorPaneStatus;
  /** How many problems hold an "invalid" edit back. */
  problemCount?: number | undefined;
  diagnostics?: readonly FileEditorDiagnostic[] | undefined;
  onChange?: ((value: string) => void) | undefined;
  /** Writes a waiting edit now, from Mod-S. */
  onSaveNow?: (() => void) | undefined;
  /** Shown as Retry while the status is "failed". */
  onRetry?: (() => void) | undefined;
  /** Replaces the path at the start of the bar; null when the page already names the file. */
  leading?: ReactNode;
  /** Controls after the save state, such as a menu of file actions. */
  actions?: ReactNode;
  /** Shown under the path bar for save errors (a `ProblemNotice`). */
  notice?: ReactNode;
  readOnlyNotice?: FileEditorPaneReadOnlyNotice | undefined;
  className?: string | undefined;
}

const STATUS_LABEL: Record<FileEditorPaneStatus, string> = {
  saved: "Saved",
  saving: "Saving…",
  failed: "Couldn't save",
  "read-only": "Read-only",
  conflict: "Conflict",
  invalid: "Not saved",
};

const STATUS_VARIANT = {
  saved: "outline",
  saving: "secondary",
  failed: "destructive",
  "read-only": "secondary",
  conflict: "destructive",
  invalid: "destructive",
} as const;

export type FileEditorToolbarProps = Pick<
  FileEditorPaneProps,
  "path" | "status" | "problemCount" | "onRetry" | "leading" | "actions"
>;

/** The pane's one bar, for a page that edits the same file in another form. */
export function FileEditorToolbar({
  path,
  status,
  problemCount,
  onRetry,
  leading,
  actions,
}: FileEditorToolbarProps) {
  return (
    <div className="flex shrink-0 items-center gap-2 border-b px-3 py-2">
      <div className="flex min-w-0 flex-1 items-center">
        {leading === undefined ? (
          <span className="truncate font-mono text-xs text-muted-foreground" title={path}>
            {path}
          </span>
        ) : (
          leading
        )}
      </div>
      <Badge variant={STATUS_VARIANT[status]} size="sm" aria-live="polite">
        {status === "read-only" ? <Lock aria-hidden /> : null}
        {STATUS_LABEL[status]}
        {status === "invalid" && problemCount
          ? ` · ${problemCount} ${problemCount === 1 ? "problem" : "problems"}`
          : null}
      </Badge>
      {status === "failed" && onRetry ? (
        <Button type="button" size="sm" variant="outline" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
      {actions}
    </div>
  );
}

export function FileEditorPane({
  path,
  value,
  language,
  status,
  problemCount,
  diagnostics,
  onChange,
  onSaveNow,
  onRetry,
  leading,
  actions,
  notice,
  readOnlyNotice,
  className,
}: FileEditorPaneProps) {
  const readOnly = status === "read-only";
  return (
    <div data-slot="file-editor-pane" className={cn("flex h-full min-h-0 flex-col", className)}>
      <FileEditorToolbar
        path={path}
        status={status}
        problemCount={problemCount}
        onRetry={onRetry}
        leading={leading}
        actions={actions}
      />
      {readOnly && readOnlyNotice ? (
        <div className="shrink-0 border-b p-3">
          <ProblemNotice
            tone="warning"
            howToResolve={readOnlyNotice.howToResolve}
            resolution={readOnlyNotice.resolution}
          >
            {readOnlyNotice.reason}
          </ProblemNotice>
        </div>
      ) : null}
      {notice ? <div className="shrink-0 border-b p-3">{notice}</div> : null}
      <div className="min-h-0 flex-1">
        <Suspense fallback={<Skeleton className="m-3 h-40" />}>
          <FileEditor
            value={value}
            language={language}
            readOnly={readOnly}
            onChange={onChange}
            onSave={onSaveNow}
            diagnostics={diagnostics}
            ariaLabel={path}
          />
        </Suspense>
      </div>
    </div>
  );
}
